import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, ArrowsLeftRight, Buildings, Check, CheckCircle, CircleNotch, Copy, DownloadSimple, FileCsv, MapPin, Steps,
  UploadSimple, User, UserPlus, Warning, WarningCircle, XCircle, type Icon,
} from '@phosphor-icons/react';
import {
  ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, ListenPanel, PanelZeile, SchalterZeile, type Ton,
} from '../../../../components/immobilien/ui';
import { useNotification } from '../../../../contexts/NotificationContext';
import { downloadBlob } from '../../../../lib/export/types';
import {
  IMPORT_FELDER, STANDARD_OPTIONEN, autoZuordnung, baueImportZeilen, baueVorschau, brauchtWindows1252, importName, objektAusDateiname, parseCsv,
  vorlageCsv, zielText, type BestehenderEig, type ImportFeld, type ImportOptionen, type ImportZeile, type ObjektKurz, type Spaltenzuordnung,
  type VorschauStatus, type VorschauZeile,
} from '../../../../lib/eigentuemer/csvImport';
import { importiereEigentuemer, type ImportErgebnis } from '../../../../lib/eigentuemer/csvImportSpeichern';

interface Datei {
  name: string;
  kopf: string[];
  zeilen: string[][];
  zuordnungen: Spaltenzuordnung[];
  vorwahlSpalte: number;
}

type Schritt = { nr: 1 } | { nr: 2; datei: Datei } | { nr: 3; ergebnis: ImportErgebnis };

const STATUS_ANZEIGE: Record<VorschauStatus, { icon: Icon; ton: 'ok' | 'warn' | 'info' | 'bad'; chip: string }> = {
  gueltig: { icon: CheckCircle, ton: 'ok', chip: 'gültig' },
  pruefen: { icon: Warning, ton: 'warn', chip: 'prüfen' },
  vorhanden: { icon: User, ton: 'info', chip: 'vorhanden' },
  dublette: { icon: Copy, ton: 'bad', chip: 'wird übersprungen' },
  fehler: { icon: XCircle, ton: 'bad', chip: 'wird übersprungen' },
};

/** Liest CSV (UTF-8, sonst Windows-1252) oder Excel (erstes Blatt). */
async function leseDatei(datei: File): Promise<{ kopf: string[]; zeilen: string[][] }> {
  if (/\.xlsx?$/i.test(datei.name)) {
    const XLSX = await import('xlsx');
    const mappe = XLSX.read(await datei.arrayBuffer(), { type: 'array' });
    const blatt = mappe.Sheets[mappe.SheetNames[0] ?? ''];
    if (!blatt) return { kopf: [], zeilen: [] };
    const roh = XLSX.utils.sheet_to_json<unknown[]>(blatt, { header: 1, raw: false, defval: '' });
    const alle = roh.map(r => r.map(c => String(c ?? '').trim()));
    const [kopf = [], ...zeilen] = alle;
    return { kopf, zeilen: zeilen.filter(r => r.some(c => c)) };
  }
  const utf8 = await datei.text();
  if (!brauchtWindows1252(utf8)) return parseCsv(utf8);
  return parseCsv(new TextDecoder('windows-1252').decode(await datei.arrayBuffer()));
}

export function ImportPanel({ bestehende, objekte, onSchliessen, onImportiert }: {
  bestehende: readonly BestehenderEig[];
  objekte: readonly ObjektKurz[];
  onSchliessen: () => void;
  /** nach dem Speichern: Liste neu laden */
  onImportiert: () => void;
}) {
  const { showToast } = useNotification();
  const [schritt, setSchritt] = useState<Schritt>({ nr: 1 });
  const kpi = schritt.nr === 1 ? 'Datei wählen' : schritt.nr === 2 ? 'Zuordnen & prüfen' : 'Fertig';
  return (
    <ListenPanel icon={FileCsv} titel="CSV-Import" sub="Eigentümer aus Excel oder CSV übernehmen" onSchliessen={onSchliessen}>
      <KpiReihe>
        <KpiTile layout="kachel" icon={Steps} ton="acc" wert={`${schritt.nr} / 3`} label={kpi} />
      </KpiReihe>
      {schritt.nr === 1 && (
        <DateiSchritt onFehler={m => showToast({ type: 'error', message: m })} onGeladen={datei => setSchritt({ nr: 2, datei })} />
      )}
      {schritt.nr === 2 && (
        <PruefSchritt key={schritt.datei.name} datei={schritt.datei} bestehende={bestehende} objekte={objekte}
          onZurueck={() => setSchritt({ nr: 1 })}
          onFertig={ergebnis => { onImportiert(); setSchritt({ nr: 3, ergebnis }); }}
          onFehler={m => showToast({ type: 'error', message: m })} />
      )}
      {schritt.nr === 3 && <ErgebnisSchritt ergebnis={schritt.ergebnis} onSchliessen={onSchliessen} onWeitere={() => setSchritt({ nr: 1 })} />}
    </ListenPanel>
  );
}

// ── 1 / 3 · Datei ──────────────────────────────────────────────────────────────

function DateiSchritt({ onGeladen, onFehler }: { onGeladen: (d: Datei) => void; onFehler: (m: string) => void }) {
  const eingabe = useRef<HTMLInputElement>(null);
  const [ziehen, setZiehen] = useState(false);
  const [liest, setLiest] = useState(false);

  const verarbeite = useCallback(async (datei: File) => {
    if (!/\.(csv|txt|tsv|xlsx|xls)$/i.test(datei.name)) { onFehler('Bitte eine CSV- oder Excel-Datei wählen.'); return; }
    setLiest(true);
    try {
      const { kopf, zeilen } = await leseDatei(datei);
      if (kopf.length === 0 || zeilen.length === 0) { onFehler('Die Datei enthält keine Kopfzeile oder keine Datenzeilen.'); return; }
      const { zuordnungen, vorwahlSpalte } = autoZuordnung(kopf);
      onGeladen({ name: datei.name, kopf, zeilen, zuordnungen, vorwahlSpalte });
    } catch (e) {
      onFehler(`Datei konnte nicht gelesen werden: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
    } finally {
      setLiest(false);
    }
  }, [onFehler, onGeladen]);

  const vorlage = () => downloadBlob(new Blob([vorlageCsv()], { type: 'text/csv;charset=utf-8;' }), 'eigentuemer_vorlage.csv');

  return (
    <InnenKarte>
      <KartenTitel>Datei</KartenTitel>
      <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
        CSV- oder Excel-Datei hierher ziehen oder auswählen. Erkannt werden Trennzeichen, Kodierung (UTF-8 / Windows-1252) und Kopfzeile.
      </span>
      <button type="button" onClick={() => eingabe.current?.click()} disabled={liest}
        onDragEnter={e => { e.preventDefault(); setZiehen(true); }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => { e.preventDefault(); setZiehen(false); }}
        onDrop={e => { e.preventDefault(); setZiehen(false); const f = e.dataTransfer.files[0]; if (f) void verarbeite(f); }}
        className="flex flex-col items-center justify-center text-center cursor-pointer"
        style={{
          gap: 8, padding: '28px 16px', borderRadius: 20, color: 'var(--ink)',
          border: `2px dashed ${ziehen ? 'var(--acc)' : 'var(--line)'}`, background: ziehen ? 'var(--acc-soft)' : 'transparent', transition: 'background .2s, border-color .2s',
        }}>
        {liest ? <CircleNotch size={28} className="animate-spin" color="var(--acc)" /> : <UploadSimple size={28} weight="bold" color="var(--acc)" />}
        <span style={{ fontSize: 14, fontWeight: 800 }}>{liest ? 'Datei wird gelesen …' : ziehen ? 'Datei loslassen' : 'Datei hierher ziehen'}</span>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>.csv, .txt, .tsv, .xlsx – Komma, Semikolon oder Tab getrennt</span>
      </button>
      <input ref={eingabe} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void verarbeite(f); }} />
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton art="akzent" icon={UploadSimple} onClick={() => eingabe.current?.click()} disabled={liest}>Datei auswählen</ImmoButton>
        <ImmoButton icon={DownloadSimple} onClick={vorlage}>Vorlage herunterladen</ImmoButton>
      </div>
    </InnenKarte>
  );
}

// ── 2 / 3 · Zuordnen & prüfen ──────────────────────────────────────────────────

const AUSWAHL_STIL: React.CSSProperties = {
  height: 32, borderRadius: 10, padding: '0 8px', fontSize: 12, fontWeight: 700, maxWidth: 170,
  border: 'none', background: 'var(--fill2)', color: 'var(--ink)', cursor: 'pointer',
};

function PruefSchritt({ datei, bestehende, objekte, onZurueck, onFertig, onFehler }: {
  datei: Datei;
  bestehende: readonly BestehenderEig[];
  objekte: readonly ObjektKurz[];
  onZurueck: () => void;
  onFertig: (e: ImportErgebnis) => void;
  onFehler: (m: string) => void;
}) {
  const [zuordnungen, setZuordnungen] = useState(datei.zuordnungen);
  const [optionen, setOptionen] = useState<ImportOptionen>(STANDARD_OPTIONEN);
  // manuelle Auswahl/Korrekturen je Vorschauzeile; bei geänderter Zuordnung verworfen
  const [umgeschaltet, setUmgeschaltet] = useState<ReadonlySet<number>>(new Set());
  const [korrektur, setKorrektur] = useState<Readonly<Record<number, Pick<ImportZeile, 'vorname' | 'nachname'>>>>({});
  const [speichert, setSpeichert] = useState(false);

  const dateiObjekt = useMemo(() => objektAusDateiname(datei.name, objekte), [datei.name, objekte]);
  const zeilen = useMemo(() => baueImportZeilen(datei.zeilen, zuordnungen, datei.vorwahlSpalte, optionen), [datei, zuordnungen, optionen]);
  const korrigiert = useMemo(() => zeilen.map((z, i) => (korrektur[i] ? { ...z, ...korrektur[i] } : z)), [zeilen, korrektur]);
  const vorschau = useMemo(() => baueVorschau(korrigiert, { bestehende, objekte, dateiObjekt, objektAuto: optionen.objektAuto }), [korrigiert, bestehende, objekte, dateiObjekt, optionen.objektAuto]);

  const dabei = (v: VorschauZeile) => v.standardAus === umgeschaltet.has(v.index);
  const auswahl = vorschau.filter(dabei);
  const neu = auswahl.filter(v => !v.vorhandenId).length;
  const gueltig = vorschau.filter(v => v.status === 'gueltig').length;
  const probleme = vorschau.filter(v => v.status !== 'gueltig').length;
  const hatName = zuordnungen.some(z => z.aktiv && (z.feld === 'nachname' || z.feld === 'firma_name' || z.umwandlung === 'name_split'));

  const zuruecksetzen = () => { setUmgeschaltet(new Set()); setKorrektur({}); };
  const aendereFeld = (i: number, feld: ImportFeld) => {
    setZuordnungen(prev => prev.map((z, j) => (j === i ? { ...z, feld, aktiv: feld !== '_skip', umwandlung: undefined, auto: false } : z)));
    zuruecksetzen();
  };
  const setzeOption = (k: keyof ImportOptionen) => (an: boolean) => {
    setOptionen(o => ({ ...o, [k]: an }));
    if (k !== 'objektAuto') zuruecksetzen();
  };
  const umschalten = (i: number) => setUmgeschaltet(prev => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const importieren = async () => {
    setSpeichert(true);
    try {
      onFertig(await importiereEigentuemer(auswahl, vorschau.length - auswahl.length));
    } catch (e) {
      onFehler(`Import fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <>
      <InnenKarte padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={datei.name}>Auto-Mapping · CSV-Spalte → Feld</KartenTitel>
        </div>
        {zuordnungen.map((z, i) => (
          <PanelZeile key={`${z.csvSpalte}-${i}`} icon={ArrowRight} ton={z.aktiv ? 'acc' : 'neutral'}
            titel={`${z.csvSpalte || `Spalte ${i + 1}`} → ${zielText(z, optionen)}`}
            sub={!z.aktiv ? 'nicht zugeordnet' : z.auto ? 'automatisch zugeordnet' : 'manuell zugeordnet'}
            chip={z.auto && z.aktiv ? { text: 'Auto', ton: 'ok' } : undefined}
            rechts={
              <select aria-label={`Feld für ${z.csvSpalte}`} value={z.umwandlung ? '' : z.feld} onChange={e => aendereFeld(i, e.target.value as ImportFeld)} style={AUSWAHL_STIL}>
                {z.umwandlung && <option value="" disabled>{z.umwandlung === 'name_split' ? 'Name-Split' : z.umwandlung === 'adresse_split' ? 'Adress-Split' : 'Telefon + Vorwahl'}</option>}
                {IMPORT_FELDER.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            } />
        ))}
        {optionen.objektAuto && (
          <PanelZeile icon={Buildings} ton={dateiObjekt ? 'acc' : 'neutral'}
            titel={dateiObjekt ? `Dateiname → ${dateiObjekt.bezeichnung ?? 'Objekt'}` : 'Objekt → über Anschrift erkennen'}
            sub={dateiObjekt ? 'alle Eigentümer werden diesem Objekt zugeordnet' : 'kein Objekt im Dateinamen erkannt'}
            chip={dateiObjekt ? { text: 'Auto', ton: 'ok' } : undefined} />
        )}
        {!hatName && (
          <PanelZeile icon={WarningCircle} ton="bad" titel="Keine Namensspalte zugeordnet" sub="Ordne mindestens Name, Nachname oder Firma zu." />
        )}
      </InnenKarte>

      <InnenKarte>
        <KartenTitel>Optionen</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 8 }}>
          <SchalterZeile icon={User} label="Name-Split (Anrede, Vor-/Nachname, Firma erkennen)" an={optionen.nameSplit} onChange={setzeOption('nameSplit')} />
          <SchalterZeile icon={ArrowsLeftRight} label="Vor- und Nachname tauschen" an={optionen.tauschen} onChange={setzeOption('tauschen')} />
          <SchalterZeile icon={MapPin} label="Adress-Split (Straße, Nr., PLZ, Stadt)" an={optionen.adressSplit} onChange={setzeOption('adressSplit')} />
          <SchalterZeile icon={Buildings} label="Objekt automatisch zuordnen" an={optionen.objektAuto} onChange={setzeOption('objektAuto')} />
        </div>
      </InnenKarte>

      <InnenKarte padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts="Zeile antippen zum Ein-/Ausschließen">{`Vorschau · ${gueltig} gültig · ${probleme} mit Problemen`}</KartenTitel>
        </div>
        <div className="flex flex-col" style={{ maxHeight: 520, overflowY: 'auto' }}>
          {vorschau.map(v => (
            <VorschauEintrag key={v.index} v={v} dabei={dabei(v)} onUmschalten={() => umschalten(v.index)}
              onKorrektur={k => setKorrektur(prev => ({ ...prev, [v.index]: k }))} />
          ))}
        </div>
      </InnenKarte>

      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={ArrowLeft} onClick={onZurueck} disabled={speichert}>Zurück</ImmoButton>
        <ImmoButton groesse={44} art="akzent" icon={speichert ? CircleNotch : Check} onClick={() => void importieren()}
          disabled={speichert || !hatName || auswahl.length === 0}>
          {speichert ? 'Importiere …' : neu === auswahl.length ? `${neu} Eigentümer importieren` : `${neu} Eigentümer importieren · ${auswahl.length - neu} zuordnen`}
        </ImmoButton>
      </div>
    </>
  );
}

function VorschauEintrag({ v, dabei, onUmschalten, onKorrektur }: {
  v: VorschauZeile;
  dabei: boolean;
  onUmschalten: () => void;
  onKorrektur: (k: Pick<ImportZeile, 'vorname' | 'nachname'>) => void;
}) {
  const a = STATUS_ANZEIGE[v.status];
  const z = v.zeile;
  const adresse = [z.strasse, z.hausnummer, z.plz, z.stadt].filter(Boolean).join(' · ');
  const objekt = v.objekt ? `Objekt erkannt: ${v.objekt.bezeichnung ?? ''}` : '';
  const sub = [adresse, objekt, ...v.hinweise].filter(Boolean).join(' · ');
  const chip: { text: string; ton: Ton } = dabei
    ? (v.standardAus ? { text: 'wird trotzdem importiert', ton: 'warn' } : { text: a.chip, ton: a.ton })
    : (v.standardAus ? { text: a.chip, ton: a.ton } : { text: 'ausgeschlossen', ton: 'neutral' });
  const zeigeKorrektur = dabei && z.pruefenName && !z.firma_name;
  return (
    <PanelZeile icon={a.icon} ton={a.ton} titel={importName(z)} sub={sub || undefined} chip={chip} aktiv={dabei} onClick={onUmschalten}
      ariaLabel={`${importName(z)} ${dabei ? 'ausschließen' : 'einschließen'}`}>
      {zeigeKorrektur && (
        <div className="flex flex-wrap items-center" style={{ gap: 8, padding: '0 6px 8px 58px' }}>
          <input aria-label="Vorname" value={z.vorname} onChange={e => onKorrektur({ vorname: e.target.value, nachname: z.nachname })} placeholder="Vorname"
            style={{ height: 32, borderRadius: 10, padding: '0 10px', fontSize: 13, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', minWidth: 0, flex: '1 1 120px' }} />
          <ImmoButton groesse={32} icon={ArrowsLeftRight} aria-label={`Vor- und Nachname tauschen: ${z.originalName}`} title="Vor- und Nachname dieser Zeile tauschen"
            onClick={() => onKorrektur({ vorname: z.nachname, nachname: z.vorname })} />
          <input aria-label="Nachname" value={z.nachname} onChange={e => onKorrektur({ vorname: z.vorname, nachname: e.target.value })} placeholder="Nachname"
            style={{ height: 32, borderRadius: 10, padding: '0 10px', fontSize: 13, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', minWidth: 0, flex: '1 1 120px' }} />
          {z.originalName && <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Original: {z.originalName}</span>}
        </div>
      )}
    </PanelZeile>
  );
}

// ── 3 / 3 · Ergebnis ───────────────────────────────────────────────────────────

function ErgebnisSchritt({ ergebnis, onSchliessen, onWeitere }: { ergebnis: ImportErgebnis; onSchliessen: () => void; onWeitere: () => void }) {
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" icon={UserPlus} ton="ok" wert={ergebnis.erstellt} label="Erstellt" />
        <KpiTile layout="kachel" icon={Buildings} ton="acc" wert={ergebnis.zuordnungen} label="Objekt-Zuordnung" />
        <KpiTile layout="kachel" icon={Copy} ton="warn" wert={ergebnis.uebersprungen} label="Übersprungen" />
      </KpiReihe>
      {ergebnis.meldungen.length > 0 && (
        <InnenKarte padding="14px 12px 10px" gap={4}>
          <div style={{ padding: '0 6px 6px' }}><KartenTitel>Details</KartenTitel></div>
          <div className="flex flex-col" style={{ maxHeight: 240, overflowY: 'auto' }}>
            {ergebnis.meldungen.map((m, i) => <PanelZeile key={i} icon={Warning} ton="warn" titel={m} />)}
          </div>
        </InnenKarte>
      )}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} art="akzent" icon={Check} onClick={onSchliessen}>Schließen</ImmoButton>
        <ImmoButton groesse={44} icon={UploadSimple} onClick={onWeitere}>Weitere Datei</ImmoButton>
      </div>
    </>
  );
}
